import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { api } from '../../services/api';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  whatsappNumber?: string;
  agreeToTerms?: string;
  telegramId?: string;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
  onSwitchToEmailVerification: (email: string, type: 'signup') => void;
}

export function SignUpForm({ onSwitchToLogin, onSwitchToEmailVerification }: SignUpFormProps) {
  const { showToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // String fields separated to avoid boolean/string type conflicts
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [telegramId, setTelegramId] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    const storedReferral = localStorage.getItem('referral_code');
    if (storedReferral) {
      setReferralCode(storedReferral);
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);

  const clearError = (field: keyof FormErrors) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Check email availability when email changes
  useEffect(() => {
    const checkEmailAvailability = async () => {
      if (email && email.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailChecking(true);
        try {
          const result = await api.checkEmail(email);
          if (result.success) {
            setEmailExists(result.exists || false);
            if (result.exists) {
              setErrors(prev => ({ ...prev, email: 'Email ini sudah terdaftar' }));
            }
          }
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setEmailChecking(false);
        }
      } else {
        setEmailExists(null);
      }
    };

    const timeoutId = setTimeout(checkEmailAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // First name
    if (!firstName.trim()) {
      newErrors.firstName = 'Nama depan wajib diisi';
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = 'Nama depan minimal 2 karakter';
    } else if (!/^[a-zA-Z\s]+$/.test(firstName)) {
      newErrors.firstName = 'Nama depan hanya boleh berisi huruf dan spasi';
    }

    // Last name
    if (!lastName.trim()) {
      newErrors.lastName = 'Nama belakang wajib diisi';
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = 'Nama belakang minimal 2 karakter';
    } else if (!/^[a-zA-Z\s]+$/.test(lastName)) {
      newErrors.lastName = 'Nama belakang hanya boleh berisi huruf dan spasi';
    }

    // Email
    if (!email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Harap masukkan alamat email yang valid';
    } else if (emailExists === true) {
      newErrors.email = 'Email ini sudah terdaftar';
    } else if (emailChecking) {
      newErrors.email = 'Harap tunggu, sedang mengecek ketersediaan email';
    }

    // Password
    if (!password) {
      newErrors.password = 'Password wajib diisi';
    } else if (password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    } else if (password.length > 128) {
      newErrors.password = 'Password terlalu panjang';
    } else if (!/(?=.*[a-z])/.test(password)) {
      newErrors.password = 'Password harus mengandung huruf kecil';
    } else if (!/(?=.*[A-Z])/.test(password)) {
      newErrors.password = 'Password harus mengandung huruf besar';
    } else if (!/(?=.*\d)/.test(password)) {
      newErrors.password = 'Password harus mengandung angka';
    }

    // Confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Harap konfirmasi password Anda';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }

    // WhatsApp
    if (!whatsappNumber.trim()) {
      newErrors.whatsappNumber = 'Nomor WhatsApp wajib diisi';
    } else if (whatsappNumber.trim().length < 10) {
      newErrors.whatsappNumber = 'Nomor WhatsApp minimal 10 angka';
    } else if (whatsappNumber.trim().length > 20) {
      newErrors.whatsappNumber = 'Nomor WhatsApp terlalu panjang';
    } else if (!/^[0-9+]+$/.test(whatsappNumber.trim())) {
      newErrors.whatsappNumber = 'Nomor WhatsApp hanya boleh berisi angka dan tanda +';
    }

    // Telegram ID
    if (!telegramId.trim()) {
      newErrors.telegramId = 'Telegram ID wajib diisi';
    }

    // Terms
    if (!agreeToTerms) {
      newErrors.agreeToTerms = 'Anda harus menyetujui syarat dan ketentuan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Harap perbaiki kesalahan di bawah ini');
      return;
    }

    setLoading(true);

    try {
      const result = await api.register(
        `${firstName} ${lastName}`,
        email,
        password,
        whatsappNumber,
        telegramId,
        referralCode
      );

      if (result.success) {
        if (result.user && result.session) {
          showToast({
            type: 'success',
            title: 'Akun Berhasil Dibuat!',
            message: 'Selamat datang!',
          });
          setTimeout(() => {
            window.location.href = '/home';
          }, 1000);
        } else {
          showToast({
            type: 'success',
            title: 'Akun Berhasil Dibuat!',
            message: 'Silakan cek email untuk verifikasi akun.',
          });
          onSwitchToEmailVerification(email, 'signup');
        }
      } else {
        toast.error(result.error || 'Gagal membuat akun');
        if (result.field) {
          const f = result.field as keyof FormErrors;
          setErrors(prev => ({ ...prev, [f]: result.error || '' }));
        }
      }
    } catch (error: unknown) {
      console.error('Signup error:', error);
      toast.error('Terjadi kesalahan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Create Account</h1>
          <p className="text-sm text-muted-foreground">Join us and get started</p>
        </div>
      </div>

      {/* Sign Up Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); clearError('firstName'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0"
            />
            {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); clearError('lastName'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0"
            />
            {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              placeholder="john.doe@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0"
            />
            {emailChecking && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
            {emailExists === false && !emailChecking && email && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
          {emailExists === false && !emailChecking && email && !errors.email && (
            <p className="text-sm text-green-600">Email is available</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
            <Input
              id="whatsappNumber"
              type="tel"
              placeholder="+6281234567890"
              value={whatsappNumber}
              onChange={(e) => { setWhatsappNumber(e.target.value); clearError('whatsappNumber'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0"
            />
            {errors.whatsappNumber && <p className="text-sm text-red-500">{errors.whatsappNumber}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telegramId">Telegram ID</Label>
            <Input
              id="telegramId"
              type="text"
              placeholder="e.g. @telegramID"
              value={telegramId}
              onChange={(e) => { setTelegramId(e.target.value); clearError('telegramId'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0"
            />
            {errors.telegramId && <p className="text-sm text-red-500">{errors.telegramId}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0 pr-10 pl-3"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
              className="bg-blue-950 border border-blue-800 focus:border-blue-600 placeholder:opacity-60 placeholder:text-gray-400 focus:placeholder:opacity-0 pr-10 pl-3"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={agreeToTerms}
              onCheckedChange={(checked) => {
                setAgreeToTerms(checked as boolean);
                clearError('agreeToTerms');
              }}
            />
            <Label htmlFor="terms" className="text-sm font-normal leading-normal py-1 flex-1">
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold transition-colors duration-200">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold transition-colors duration-200">
                  Privacy Policy
                </a>
              </span>
            </Label>
          </div>
          {errors.agreeToTerms && <p className="text-sm text-red-500">{errors.agreeToTerms}</p>}
        </div>

        <Button
          type="submit"
          className="w-full bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 disabled:bg-muted disabled:text-muted-foreground disabled:border-muted transition-all duration-200"
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>

      {/* Login Link */}
      <div className="text-center">
        <span className="text-sm text-muted-foreground">Already have an account? </span>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-sm text-foreground hover:text-muted-foreground font-medium transition-colors duration-200"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}